import * as React from 'react';
import Dropdown from 'antd/lib/dropdown';
import Checkbox from 'antd/lib/checkbox';
import Space from 'antd/lib/space';
import { CaretDownOutlined } from '@ant-design/icons';
import { antGrey } from '../styles/globalStyles';
import { DestinationCounter } from './DestinationCounter';
import { Destination } from '@/types/destinations';
import { displayStringForDestination } from '@/helpers/displayHelpers';
import Avatar from 'antd/lib/avatar';
import { flatMap } from 'lodash';
import { MenuProps } from 'antd/lib/menu';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';
import Divider from 'antd/lib/divider';
import theme from 'antd/lib/theme';
import WorkflowsPlatformIcon from '../icons/WorkflowsPlatformIcon';

interface DestinationSelectorProps {
    destinations: Destination[];
    selectedDestinationIds: string[];
    onSelectionChange: (selectedIds: string[]) => void;
    requiresConfirmation?: boolean;
    isLoading?: boolean;
}

const MAX_VISIBLE_SELECTED_DESTINATIONS_LIMIT = 5;

export const DestinationSelector: React.FC<DestinationSelectorProps> = ({
    destinations,
    selectedDestinationIds,
    onSelectionChange,
    requiresConfirmation = false,
    isLoading,
}) => {
    const { token } = theme.useToken();
    let [selectedKeys, setSelectedKeys] = React.useState<string[]>(selectedDestinationIds);

    if (!requiresConfirmation) {
        // if confirmation is not required, do not use local state
        selectedKeys = selectedDestinationIds;
        setSelectedKeys = onSelectionChange;
    }

    const [isOpen, setIsOpen] = React.useState(false);
    const toggleDestination = (destId: string) => {
        if (selectedKeys.includes(destId)) {
            const filteredKeys = selectedKeys.filter((id) => id !== destId);
            // do not allow empty selection
            if (filteredKeys.length > 0) {
                setSelectedKeys(filteredKeys);
            }
        } else {
            setSelectedKeys([...selectedKeys, destId]);
        }
    };

    const toggleAll = () => {
        setSelectedKeys(destinations.map((dest) => dest.stepConfigId));
    };

    const handleOpenChange = (open: boolean, info?: { source: 'trigger' | 'menu' }) => {
        if (requiresConfirmation) {
            const { source } = info;
            if (!open && source === 'trigger') {
                // If confirmation is required and menu was closed without it, reset selectedKeys to reflect original source
                // of truth in props; this way it's obvious to the user if they've closed the dropdown without applying changes
                setSelectedKeys(selectedDestinationIds);
            }
        }
        setIsOpen(open);
    };

    const allSelected = destinations.length > 0 && selectedKeys.length === destinations.length;

    // Get selected destinations as full objects
    const selectedDestinations = destinations.filter((dest) => selectedKeys.includes(dest.stepConfigId));

    const items: MenuProps['items'] = [
        {
            key: 'all',
            onClick: () => toggleAll(),
            label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <Checkbox checked={allSelected} onChange={toggleAll} />
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 14,
                            color: antGrey.grey8,
                        }}
                    >
                        <DestinationCounter destinations={destinations} size={24} />
                        <span style={{ marginLeft: 10 }}>All destinations</span>
                    </div>
                </div>
            ),
        },
        {
            type: 'divider',
        },
        ...flatMap(destinations, (destination, index) => [
            {
                key: destination.stepConfigId,
                onClick: () => toggleDestination(destination.stepConfigId),
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <Checkbox
                            checked={selectedKeys.includes(destination.stepConfigId)}
                            style={{ marginRight: 10 }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 14,
                                color: antGrey.grey8,
                            }}
                        >
                            {!!destination.iconURL || !!destination.name ?
                                <Avatar
                                    shape={'square'}
                                    size={24}
                                    src={destination.iconURL}
                                    style={{
                                        border: 1,
                                        borderRadius: 4,
                                    }}
                                >
                                    {destination.name.charAt(0)}
                                </Avatar>
                            :   <WorkflowsPlatformIcon platform={destination.platform} width={24} height={24} />}
                            <span style={{ marginLeft: 10 }}>
                                {displayStringForDestination(
                                    destination.platform,
                                    destination.appStoreType,
                                    destination.name,
                                )}
                            </span>
                        </div>
                    </div>
                ),
            },
            ...((index !== destinations.length - 1 ? [{ type: 'divider' }] : []) as MenuProps['items']),
        ]),
    ];

    const areSelectedDestinationsMoreThanLimit = selectedKeys.length > MAX_VISIBLE_SELECTED_DESTINATIONS_LIMIT;

    return (
        <Dropdown
            menu={{
                items,
                selectable: true,
                selectedKeys: [],
                multiple: true,
            }}
            trigger={['click']}
            onOpenChange={handleOpenChange}
            open={isOpen}
            dropdownRender={
                requiresConfirmation ?
                    (menu) => (
                        <div
                            style={{
                                backgroundColor: antGrey.grey4,
                                borderRadius: 8,
                                boxShadow: token.boxShadowSecondary,
                            }}
                        >
                            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                                {React.cloneElement(
                                    menu as React.ReactElement<{
                                        style: React.CSSProperties;
                                    }>,
                                    {
                                        style: {
                                            borderBottomLeftRadius: 0,
                                            borderBottomRightRadius: 0,
                                            boxShadow: 'none',
                                        },
                                    },
                                )}
                            </div>
                            <Divider style={{ margin: 0 }} />
                            <div style={{ padding: 8, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    type="primary"
                                    size="small"
                                    loading={!!isLoading}
                                    onClick={() => {
                                        onSelectionChange(selectedKeys);
                                        setIsOpen(false);
                                    }}
                                >
                                    View selected
                                </Button>
                            </div>
                        </div>
                    )
                :   undefined
            }
        >
            <div
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    border: `2px solid ${antGrey.grey5}`,
                    borderRadius: 8,
                    backgroundColor: antGrey.grey2,
                }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                    <Space>
                        <DestinationCounter destinations={selectedDestinations} />
                        <Text
                            style={{
                                color: antGrey.grey8,
                                fontSize: 16,
                                fontWeight: 600,
                                paddingRight: 8,
                                paddingLeft: 8,
                            }}
                        >
                            {selectedKeys.length === destinations.length ?
                                'All destinations'
                            : selectedKeys.length === 1 ?
                                `${displayStringForDestination(selectedDestinations[0]?.platform, selectedDestinations[0]?.appStoreType, selectedDestinations[0]?.name)}`
                            :   `Selected destinations`}
                        </Text>
                        <Space>
                            {selectedKeys.length > 1 &&
                                selectedKeys.slice(0, MAX_VISIBLE_SELECTED_DESTINATIONS_LIMIT).map((key, i) => {
                                    const destination = destinations.find((dest) => dest.stepConfigId === key);
                                    return (
                                        <div key={`dest=${key}&i=${i}`}>
                                            {!destination?.iconURL && !destination?.name && destination?.platform ?
                                                <WorkflowsPlatformIcon
                                                    platform={destination.platform}
                                                    width={24}
                                                    height={24}
                                                />
                                            :   <Avatar
                                                    shape={'square'}
                                                    size={24}
                                                    src={destination?.iconURL}
                                                    style={{
                                                        border: 1,
                                                        borderRadius: 4,
                                                    }}
                                                >
                                                    {destination.name.charAt(0)}
                                                </Avatar>
                                            }
                                        </div>
                                    );
                                })}

                            {areSelectedDestinationsMoreThanLimit && (
                                <Text
                                    style={{
                                        color: antGrey.grey6,
                                        fontSize: 14,
                                        padding: '4px 6px',
                                        backgroundColor: antGrey.grey4,
                                        border: `1px solid ${antGrey.grey5}`,
                                        borderRadius: 4,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {`+${selectedKeys.length - MAX_VISIBLE_SELECTED_DESTINATIONS_LIMIT}`}
                                </Text>
                            )}
                        </Space>
                    </Space>
                    <CaretDownOutlined style={{ color: antGrey.grey6, fontSize: 24 }} />
                </div>
            </div>
        </Dropdown>
    );
};
